import {
  Button,
  Checkbox,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { pluralize, pushInNextLoop } from "src/utils";
import {
  useCopyCostsModal_CopyPlanBasedBidCostsMutation,
  useCopyCostsModal_SaveBidContractMutation,
} from "./CopyCostsModal.generated";
import { buildSaveInput, EditFormValues } from "./PlanBasedBidsTableV2";
import { PlanBasedBidsTableV2_BidPackageFragment } from "./PlanBasedBidsTableV2.generated";

type CopyCostsModalProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  bidContractId: string;
  formState: ObjectState<EditFormValues>;
  /** The POF tab being viewed, i.e. the default source to copy from. */
  sourceProductOfferingId: string;
};

export function CopyCostsModal(props: CopyCostsModalProps) {
  const { bidPackage, bidContractId, formState } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const navigate = useNavigate();
  const [saveBidContract] = useCopyCostsModal_SaveBidContractMutation();
  const [copyCosts] = useCopyCostsModal_CopyPlanBasedBidCostsMutation();
  const [sourceId, setSourceId] = useState(props.sourceProductOfferingId);
  const sortedPofs = bidPackage.productOfferings.sortBy((pof) => pof.id);
  const [targetIds, setTargetIds] = useState(() => otherPofIds(props.sourceProductOfferingId));

  function otherPofIds(sourceId: string) {
    return sortedPofs.filter((pof) => pof.id !== sourceId).map((pof) => pof.id);
  }

  async function onCopy() {
    // Persist any unsaved edits first so the copy gets the costs entered so far
    const planBasedLineItems = buildSaveInput(formState);
    if (planBasedLineItems.nonEmpty) {
      await saveBidContract({ variables: { input: { id: bidContractId, planBasedLineItems } } });
      formState.commitChanges();
    }
    await copyCosts({
      variables: { input: { bidContractId, sourceProductOfferingId: sourceId, targetProductOfferingIds: targetIds } },
    });
    triggerNotice({
      icon: "success",
      message: `Costs copied to ${targetIds.length} ${pluralize(targetIds.length, "offering")}`,
    });
    closeModal();
    pushInNextLoop(navigate, createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id));
  }

  return (
    <>
      <ModalHeader>Copy Costs to Other Offerings</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div>
            Copying costs to other offerings will <span css={Css.smSb.$}>overwrite</span> existing costs if the source
            offering has lines that match
          </div>
          {formState.dirty && (
            <div css={Css.gray700.$} data-testid="unsavedNotice">
              Your unsaved changes will be saved and included in the copy.
            </div>
          )}
          <SelectField
            label="Copy from"
            value={sourceId}
            options={sortedPofs}
            onSelect={(id) => {
              if (!id) return;
              setSourceId(id);
              setTargetIds(otherPofIds(id));
            }}
          />
          <div css={Css.df.fdc.gap2.py1.$}>
            <div css={Css.gray700.$}>Copy to</div>
            {sortedPofs
              .filter((pof) => pof.id !== sourceId)
              .map((pof) => (
                <Checkbox
                  key={pof.id}
                  label={pof.name}
                  selected={targetIds.includes(pof.id)}
                  onChange={(selected) =>
                    setTargetIds((ids) => (selected ? [...ids, pof.id] : ids.filter((id) => id !== pof.id)))
                  }
                />
              ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Copy Costs" disabled={targetIds.isEmpty} onClick={onCopy} />
      </ModalFooter>
    </>
  );
}
